import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  ChevronDown,
  MoreHorizontal,
  Eye,
  Trash2,
  Phone,
  MessageCircle,
  PackagePlus,
  History,
  ChevronLeft,
  ChevronRight,
  Calendar,
  X,
  Tag,
  Layout,
} from "lucide-react";
import ExpandableSearchBar from "../components/ExpandableSearchBar";
import ThemeSelect from "../components/ThemeSelect";
import { useTheme } from "../context/ThemeContext";
import { ordersApi } from "../services/api";
import { message } from "antd";
import DateRangeFilter from "../components/DateRangeFilter";
import Pagination from "../components/Pagination";
import ExcelExportButton from "../components/ExcelExportButton";

const DraftOrders = () => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === "#FFFFFF";
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeActionId, setActiveActionId] = useState(null);
  const actionMenuRef = useRef(null);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setActiveActionId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchDraftOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      // ── Current user lo ──
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const isAdmin = user?.role?.toUpperCase() === "ADMIN";

      // ── Admin → sab drafts, Employee → sirf apne ──
      const params = isAdmin ? {} : { userId: user.id };
      const response = await ordersApi.getDraftOrders(params);

      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.content)
          ? response.content
          : Array.isArray(response?.data)
            ? response.data
            : [];

      setOrders(data);
    } catch (err) {
      console.error("Error fetching draft orders:", err);
      setError(err.message || "Failed to load draft orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDraftOrders();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleConvertToOrder = async (orderId) => {
    if (
      !window.confirm(
        "Are you sure you want to convert this draft to an active order?",
      )
    )
      return;
    try {
      setLoading(true);
      await ordersApi.convertDraftToOrder(orderId);
      message.success("Draft converted to order successfully!");
      fetchDraftOrders();
    } catch (err) {
      console.error("Error converting draft:", err);
      message.error(
        `Failed to convert draft: ${err.message || "Unknown error"}`,
      );
    } finally {
      setLoading(false);
      setActiveActionId(null);
    }
  };

  const handleDeleteDraft = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this draft?")) return;
    try {
      setLoading(true);
      await ordersApi.deleteDraftOrder(orderId);
      message.success("Draft deleted successfully!");
      fetchDraftOrders();
    } catch (err) {
      console.error("Error deleting draft:", err);
      message.error(
        `Failed to delete draft: ${err.message || "Unknown error"}`,
      );
    } finally {
      setLoading(false);
      setActiveActionId(null);
    }
  };

  const handleDownloadExcel = () => {
    try {
      setIsDownloading(true);
      const csvContent =
        "data:text/csv;charset=utf-8," +
        [
          "Order ID,Date,Customer Name,Phone,Product,Category,Amount,Campaign,Status,Created By,Remarks",
        ]
          .concat(
            filteredOrders.map(
              (o) =>
                `${o.id},${o.createdAt ? new Date(o.createdAt).toLocaleDateString() : o.date || "N/A"},${(o.customerName || o.lead?.customerName || o.name || "N/A").replace(/,/g, " ")},${o.contactNumber || o.lead?.contactNumber || o.phone || "N/A"},${(o.product || (o.orderItems && o.orderItems.map((i) => i.productName).join(", ")) || "Ayurvedic (Draft)").replace(/,/g, " ")},${o.category || "Health"},${o.grandTotal || o.amount || "0"},${o.campaign || o.orderType || "Manual"},Draft,${o.createdBy || o.orderBy || "System"},${(o.remarks || "No remarks").replace(/,/g, " ")}`,
            ),
          )
          .join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute(
        "download",
        `Draft_Orders_${new Date().toISOString().split("T")[0]}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      message.success("Draft orders exported successfully");
    } catch (err) {
      message.error("Failed to export draft orders");
    } finally {
      setIsDownloading(false);
    }
  };

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    dateRange: "",
    customStartDate: "",
    customEndDate: "",
    campaign: "",
    category: "",
    product: "",
  });
  const [tempFilters, setTempFilters] = useState({
    dateRange: "",
    customStartDate: "",
    customEndDate: "",
    campaign: "",
    category: "",
    product: "",
  });

  // Unique options for dropdowns
  const uniqueCampaigns = [
    ...new Set(orders.map((o) => o.campaign || o.orderType).filter(Boolean)),
  ];
  const uniqueCategories = [
    ...new Set(orders.map((o) => o.category).filter(Boolean)),
  ];
  const uniqueProducts = [
    ...new Set(orders.map((o) => o.product).filter(Boolean)),
  ];

  const filteredOrders = orders.filter((order) => {
    // 1. Search Term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const name = (
        order.customerName ||
        order.lead?.customerName ||
        order.name ||
        ""
      ).toLowerCase();
      const phone = (
        order.contactNumber ||
        order.lead?.contactNumber ||
        order.phone ||
        ""
      ).toString();
      const id = (order.id || "").toString();
      if (!name.includes(term) && !phone.includes(term) && !id.includes(term)) {
        return false;
      }
    }

    // 2. Date Range
    if (activeFilters.dateRange && activeFilters.dateRange !== "all") {
      const orderDate = order.createdAt
        ? new Date(order.createdAt)
        : order.date
          ? new Date(order.date)
          : null;
      if (!orderDate) return false;

      let startDate, endDate;
      const startOfDay = (d) => {
        const nd = new Date(d);
        nd.setHours(0, 0, 0, 0);
        return nd;
      };
      const endOfDay = (d) => {
        const nd = new Date(d);
        nd.setHours(23, 59, 59, 999);
        return nd;
      };

      switch (activeFilters.dateRange) {
        case "today":
          startDate = startOfDay(new Date());
          endDate = endOfDay(new Date());
          break;
        case "yesterday":
          const yest = new Date();
          yest.setDate(yest.getDate() - 1);
          startDate = startOfDay(yest);
          endDate = endOfDay(yest);
          break;
        case "this_week":
          const curr = new Date();
          const day = curr.getDay();
          const diff = curr.getDate() - day + (day === 0 ? -6 : 1);
          startDate = startOfDay(new Date(curr.setDate(diff)));
          endDate = endOfDay(new Date());
          break;
        case "last_week":
          const lastWk = new Date();
          lastWk.setDate(lastWk.getDate() - 7);
          startDate = startOfDay(lastWk);
          endDate = endOfDay(new Date());
          break;
        case "this_month":
          const nowMonth = new Date();
          startDate = startOfDay(
            new Date(nowMonth.getFullYear(), nowMonth.getMonth(), 1),
          );
          endDate = endOfDay(
            new Date(nowMonth.getFullYear(), nowMonth.getMonth() + 1, 0),
          );
          break;
        case "last_month":
          const lastMonth = new Date();
          startDate = startOfDay(
            new Date(lastMonth.getFullYear(), lastMonth.getMonth() - 1, 1),
          );
          endDate = endOfDay(
            new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 0),
          );
          break;
        case "custom":
          if (activeFilters.customStartDate && activeFilters.customEndDate) {
            startDate = startOfDay(new Date(activeFilters.customStartDate));
            endDate = endOfDay(new Date(activeFilters.customEndDate));
          }
          break;
        default:
          break;
      }

      if (startDate && endDate) {
        if (orderDate < startDate || orderDate > endDate) return false;
      }
    }

    // 3. Campaign
    if (activeFilters.campaign && activeFilters.campaign !== "all") {
      if ((order.campaign || order.orderType) !== activeFilters.campaign)
        return false;
    }

    // 4. Category
    if (activeFilters.category && activeFilters.category !== "all") {
      if (order.category !== activeFilters.category) return false;
    }

    // 5. Product
    if (activeFilters.product && activeFilters.product !== "all") {
      if (order.product !== activeFilters.product) return false;
    }

    return true;
  });

  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * ordersPerPage,
    currentPage * ordersPerPage,
  );

  return (
    <div
      className={`p-6 min-h-screen transition-colors duration-300 ${isDark ? "bg-[#0f172a]" : "bg-gray-50"}`}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1
          className={`text-2xl desktop:text-3xl font-black uppercase tracking-wider ${isDark ? "text-white" : "text-gray-800"}`}
        >
          Draft Orders
        </h1>
        <div className="flex items-center gap-3">
          <ExcelExportButton
            onClick={handleDownloadExcel}
            isDownloading={isDownloading}
            label="Download"
            className={`!px-6 !py-2.5 !rounded-xl font-bold uppercase tracking-wider text-xs shadow-lg transition-all active:scale-95 border ${
              isDark
                ? "bg-[#111827] border-white/5 text-white hover:bg-white/10"
                : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
          />
          <button
            onClick={() => {
              setTempFilters(activeFilters);
              setIsFilterModalOpen(true);
            }}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold uppercase tracking-wider text-xs shadow-lg transition-all active:scale-95 border ${
              isDark
                ? "bg-[#111827] border-white/5 text-white hover:bg-white/10"
                : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
            style={{
              backgroundColor: Object.values(activeFilters).some(
                (v) => v && v !== "all",
              )
                ? currentTheme.color
                : undefined,
              color: Object.values(activeFilters).some((v) => v && v !== "all")
                ? "#FFF"
                : undefined,
              borderColor: Object.values(activeFilters).some(
                (v) => v && v !== "all",
              )
                ? currentTheme.color
                : undefined,
            }}
          >
            <Filter className="w-4 h-4" />
            <span>Filter</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div
        className={`mb-6 p-4 rounded-2xl transition-all shadow-lg border ${isDark ? "bg-[#111827] border-white/5" : "bg-white border-gray-100"}`}
      >
        <ExpandableSearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search by Lead ID, Customer Name or Phone..."
        />
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          <span className="ml-3 text-gray-600">Loading draft orders...</span>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <>
          <div
            className={`overflow-x-auto border rounded-2xl shadow-xl transition-all ${isDark ? "bg-[#111827] border-white/5" : "bg-white border-gray-200"}`}
          >
            <table
              className={`min-w-full divide-y ${isDark ? "divide-white/5" : "divide-gray-200"}`}
            >
              <thead
                style={{ background: isDark ? "#111827" : currentTheme.color }}
              >
                <tr>
                  {[
                    "Order ID",
                    "Date",
                    "Name",
                    "Product",
                    "Category",
                    "Amount",
                    "Campaign",
                    "Status",
                    "Order By",
                    "Remarks",
                    "Action",
                  ].map((head, i) => (
                    <th
                      key={head}
                      scope="col"
                      className={`px-6 py-5 text-left text-[10px] font-black uppercase tracking-wider ${isDark ? "text-gray-400 border-white/5" : "text-white border-white/10"} ${i < 10 ? "border-r" : ""}`}
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody
                className={`${isDark ? "bg-transparent divide-white/5" : "bg-white divide-gray-200"} divide-y`}
              >
                {paginatedOrders.map((order, index) => (
                  <tr
                    key={index}
                    className={`transition-all ${isDark ? "hover:bg-white/5" : "hover:bg-gray-50"}`}
                  >
                    <td
                      className={`px-6 py-4 text-sm font-black border-r ${isDark ? "text-gray-300 border-white/5" : "text-gray-900 border-gray-100"}`}
                    >
                      {order.id}
                    </td>
                    <td
                      className={`px-6 py-4 text-xs font-black border-r whitespace-nowrap ${isDark ? "text-gray-400 border-white/5" : "text-gray-500 border-gray-100"}`}
                    >
                      {order.createdAt
                        ? new Date(order.createdAt).toLocaleDateString()
                        : order.date || "N/A"}
                    </td>
                    <td
                      className={`px-6 py-4 border-r ${isDark ? "border-white/5" : "border-gray-100"}`}
                    >
                      <div className="flex flex-col">
                        <span
                          className={`text-sm font-black ${isDark ? "text-gray-200" : "text-gray-800"}`}
                        >
                          {order.customerName ||
                            order.lead?.customerName ||
                            order.name ||
                            "N/A"}
                        </span>
                        <div className="flex items-center gap-1 text-[10px] text-gray-500 mt-1 font-black uppercase tracking-wider">
                          <Phone className="w-3 h-3 rotate-90" />
                          <span>
                            {order.contactNumber ||
                              order.lead?.contactNumber ||
                              order.phone ||
                              "N/A"}
                          </span>
                          <MessageCircle className="w-3 h-3 text-emerald-500 fill-emerald-500/10 ml-1" />
                        </div>
                      </div>
                    </td>
                    <td
                      className={`px-6 py-4 text-xs font-black border-r max-w-xs ${isDark ? "text-gray-400 border-white/5" : "text-gray-600 border-gray-100"}`}
                    >
                      {order.product ||
                        (order.orderItems &&
                          order.orderItems
                            .map((i) => i.productName)
                            .join(", ")) ||
                        "Ayurvedic (Draft)"}
                    </td>
                    <td
                      className={`px-6 py-4 text-xs font-black border-r ${isDark ? "text-gray-400 border-white/5" : "text-gray-600 border-gray-100"}`}
                    >
                      {order.category || "Health"}
                    </td>
                    <td
                      className={`px-6 py-4 text-sm font-black border-r ${isDark ? "text-emerald-400 border-white/5" : "text-teal-600 border-gray-100"}`}
                    >
                      ₹ {order.grandTotal || order.amount || "0"}
                    </td>
                    <td
                      className={`px-6 py-4 text-xs font-black border-r ${isDark ? "text-gray-400 border-white/5" : "text-gray-700 border-gray-100"}`}
                    >
                      {order.campaign || order.orderType || "Manual"}
                    </td>
                    <td
                      className={`px-6 py-4 border-r ${isDark ? "border-white/5" : "border-gray-100"}`}
                    >
                      <span
                        className={`text-[10px] font-black px-2 py-1 rounded-full border uppercase tracking-wider ${isDark ? "bg-orange-500/10 border-orange-500/20 text-orange-400" : "bg-orange-50 border-orange-200 text-orange-500"}`}
                      >
                        Draft
                      </span>
                    </td>
                    <td
                      className={`px-6 py-4 text-xs font-black border-r ${isDark ? "text-gray-400 border-white/5" : "text-gray-700 border-gray-100"}`}
                    >
                      <div className="flex flex-col">
                        <span
                          className={`${isDark ? "text-gray-300" : "text-gray-800"}`}
                        >
                          {order.createdBy || order.orderBy || "System"}
                        </span>
                        <span className="text-[10px] text-gray-500 uppercase tracking-widest">
                          Emp: {order.empId || order.employeeId || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td
                      className={`px-6 py-4 text-xs font-black border-r ${isDark ? "text-gray-400 border-white/5" : "text-gray-700 border-gray-100"}`}
                    >
                      <div
                        className="max-w-[150px] truncate"
                        title={order.remarks}
                      >
                        {order.remarks || "No remarks"}
                      </div>
                    </td>
                    <td className="px-6 py-4 desktop:px-8 desktop:py-6 text-sm desktop:text-base text-gray-700 relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveActionId(
                            activeActionId === index ? null : index,
                          );
                        }}
                        className="text-teal-600 hover:text-teal-800 transition-colors"
                      >
                        <Eye className="w-5 h-5 desktop:w-6 desktop:h-6" />
                      </button>

                      {/* Action Popup */}
                      {activeActionId === index && (
                        <div
                          ref={actionMenuRef}
                          className={`absolute right-full top-0 mt-[-10px] mr-2 w-48 desktop:w-56 rounded-2xl shadow-2xl border z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 ${isDark ? "bg-[#111827] border-white/10 shadow-black/50" : "bg-white border-gray-100"}`}
                        >
                          <div
                            className={`absolute right-[-6px] top-4 w-3 h-3 transform rotate-45 border-t border-r ${isDark ? "bg-[#111827] border-white/10" : "bg-white border-gray-100"}`}
                          ></div>
                          <div className="py-2 relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConvertToOrder(order.id);
                              }}
                              className={`w-full text-left px-4 py-3 text-sm desktop:text-base flex items-center gap-3 border-b transition-colors ${isDark ? "text-teal-400 hover:bg-white/10 border-white/5" : "text-teal-700 hover:bg-teal-50 border-gray-50"}`}
                            >
                              <PackagePlus
                                className={`w-4 h-4 desktop:w-5 desktop:h-5 ${isDark ? "text-teal-400" : "text-teal-600"}`}
                              />
                              Convert to order
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/orders/add", {
                                  state: {
                                    leadData: {
                                      customerName: order.name,
                                      contactNumber:
                                        order.contactNumber || order.phone,
                                      ...order,
                                    },
                                  },
                                });
                              }}
                              className={`w-full text-left px-4 py-3 text-sm desktop:text-base flex items-center gap-3 border-b transition-colors ${isDark ? "text-gray-300 hover:bg-white/10 border-white/5" : "text-gray-700 hover:bg-gray-50 border-gray-50"}`}
                            >
                              <Eye
                                className={`w-4 h-4 desktop:w-5 desktop:h-5 ${isDark ? "text-teal-400" : "text-teal-600"}`}
                              />
                              View/Edit Draft
                            </button>
                            <button
                              className={`w-full text-left px-4 py-3 text-sm desktop:text-base flex items-center gap-3 border-b transition-colors ${isDark ? "text-gray-300 hover:bg-white/10 border-white/5" : "text-gray-700 hover:bg-gray-50 border-gray-50"}`}
                            >
                              <History
                                className={`w-4 h-4 desktop:w-5 desktop:h-5 ${isDark ? "text-teal-400" : "text-teal-600"}`}
                              />
                              Lead History
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDraft(order.id);
                              }}
                              className={`w-full text-left px-4 py-3 text-sm desktop:text-base flex items-center gap-3 transition-colors ${isDark ? "text-red-400 hover:bg-red-500/20" : "text-red-600 hover:bg-red-50"}`}
                            >
                              <Trash2
                                className={`w-4 h-4 desktop:w-5 desktop:h-5 ${isDark ? "text-red-400" : "text-red-500"}`}
                              />
                              Delete Draft
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredOrders.length}
              itemsPerPage={ordersPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </>
      )}

      {/* Filter Modal */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
            <div
              className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0"
              style={{ backgroundColor: currentTheme.color }}
            >
              <h3 className="text-lg font-bold text-white">
                Filter Draft Orders
              </h3>
              <button
                onClick={() => setIsFilterModalOpen(false)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 overflow-visible flex-grow">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Date Range */}
                <div className="col-span-1 md:col-span-2">
                  <DateRangeFilter
                    label="Date Range"
                    placeholder="Select Date Range"
                    preset={tempFilters.dateRange}
                    startDate={tempFilters.customStartDate}
                    endDate={tempFilters.customEndDate}
                    onChange={({ preset, startDate, endDate }) =>
                      setTempFilters({
                        ...tempFilters,
                        dateRange: preset,
                        customStartDate: startDate,
                        customEndDate: endDate,
                      })
                    }
                  />
                </div>

                {/* Campaign */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campaign
                  </label>
                  <ThemeSelect
                    value={tempFilters.campaign}
                    onChange={(val) =>
                      setTempFilters({ ...tempFilters, campaign: val })
                    }
                    options={[
                      { label: "All Campaigns", value: "" },
                      ...uniqueCampaigns.map((c) => ({ label: c, value: c })),
                    ]}
                    placeholder="All Campaigns"
                    className="w-full"
                    icon={Layout}
                  />
                </div>

                {/* Product */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product
                  </label>
                  <ThemeSelect
                    value={tempFilters.product}
                    onChange={(val) =>
                      setTempFilters({ ...tempFilters, product: val })
                    }
                    options={[
                      { label: "All Products", value: "" },
                      ...uniqueProducts.map((p) => ({ label: p, value: p })),
                    ]}
                    placeholder="All Products"
                    className="w-full"
                    icon={Tag}
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <ThemeSelect
                    value={tempFilters.category}
                    onChange={(val) =>
                      setTempFilters({ ...tempFilters, category: val })
                    }
                    options={[
                      { label: "All Categories", value: "" },
                      ...uniqueCategories.map((c) => ({ label: c, value: c })),
                    ]}
                    placeholder="All Categories"
                    className="w-full"
                    icon={Tag}
                  />
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
              <button
                onClick={() => {
                  setTempFilters({
                    dateRange: "",
                    customStartDate: "",
                    customEndDate: "",
                    campaign: "",
                    category: "",
                    product: "",
                  });
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Reset
              </button>
              <button
                onClick={() => {
                  setActiveFilters(tempFilters);
                  setIsFilterModalOpen(false);
                  setCurrentPage(1);
                }}
                className="px-6 py-2 text-white rounded-lg font-medium shadow-sm hover:shadow transition-all border border-white/20"
                style={{ backgroundColor: currentTheme.color }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftOrders;
